import React, { useEffect, useState, useRef, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Typography,
  Paper,
  Grid,
  Chip,
  Dialog,
  DialogContent,
  IconButton,
  Breadcrumbs,
  Link,
  Divider,
  TextField,
  Alert,
  Tooltip,
  DialogTitle,
  DialogActions,
  FormControl,
  FormLabel,
  CircularProgress
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import VerifiedIcon from '@mui/icons-material/Verified';
import ThumbDownIcon from '@mui/icons-material/ThumbDown';
import PhotoCamera from '@mui/icons-material/PhotoCamera';
import { Issue, IssueUpdate } from '../../types/issue';
import { issueApi } from '../../api/issueApi';
import { useProjectStore } from '../../store/projectStore';
import { useLineStore } from '../../store/lineStore';
import { userApi } from '../../api/userApi';
import { projectUserApi } from '../../api/projectUserApi';
import { BorderRight } from '@mui/icons-material';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { getImageUrl, loadImageWithFallback } from '../../utils/imageUrl';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { format } from 'date-fns';
import { useAuthStore } from '../../store/authStore';
import { isIssuer, isAdmin, isCmPic, isApprover, isReviewer, hasRole } from '../../utils/permissions';
import { User, UserRole } from '../../types/models';

const ProjectIssueDetail: React.FC = () => {
  const { id: projectId, issueId } = useParams<{ id: string; issueId: string }>();
  const navigate = useNavigate();
  const [issue, setIssue] = useState<Issue | null>(null);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { currentProject, fetchProject, isLoading: projectLoading } = useProjectStore();
  const contentRef = useRef<HTMLDivElement>(null);
  const { user } = useAuthStore();
  const { lines, fetchLines } = useLineStore();
  const [users, setUsers] = useState<User[]>([]);
  const [projectUserIds, setProjectUserIds] = useState<number[]>([]);

  // Add states for approval dialogs
  const [approveDialogOpen, setApproveDialogOpen] = useState(false);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [counterMeasure, setCounterMeasure] = useState('');
  const [finalCounterMeasure, setFinalCounterMeasure] = useState('');
  const [notes, setNotes] = useState('');
  const [submitLoading, setSubmitLoading] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  
  // States for photo uploads
  const [selectedCounterMeasureFiles, setSelectedCounterMeasureFiles] = useState<File[]>([]);
  const [isCompressing, setIsCompressing] = useState(false);
  const counterMeasureFileInputRef = useRef<HTMLInputElement>(null);

  // Add a new state for image loading status
  const [loadedImages, setLoadedImages] = useState<Record<string, string>>({});
  
  // Function to compress images before upload
  const compressImage = async (file: File): Promise<File> => {
    try {
      setIsCompressing(true);
      
      // Import the compression library dynamically
      const imageCompression = (await import('browser-image-compression')).default;
      
      const options = {
        maxSizeMB: 1,
        maxWidthOrHeight: 1920,
        useWebWorker: true
      };
      
      const compressedBlob = await imageCompression(file, options);
      
      // Create a new File object from the compressed blob
      const newFile = new File([compressedBlob], file.name, {
        type: compressedBlob.type,
        lastModified: file.lastModified
      });
      
      return newFile;
    } catch (error) {
      console.error('Error compressing image:', error);
      // If compression fails, return the original file
      return file;
    } finally {
      setIsCompressing(false);
    }
  };

  // Handle file selection for counter measure photos
  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files) {
      const files = Array.from(event.target.files);
      
      setIsCompressing(true);
      
      // Compress each image
      const compressedFiles = await Promise.all(
        files.map(file => compressImage(file))
      );
      
      // Update state with compressed files
      setSelectedCounterMeasureFiles((prev) => [...prev, ...compressedFiles]);
      
      setIsCompressing(false);
    }
  };

  // Handle removing a selected file
  const handleRemoveFile = (index: number) => {
    setSelectedCounterMeasureFiles((prev) => prev.filter((_, i) => i !== index));
  };
  
  // Add a function to handle image loading with fallback
  const handleImageLoad = async (fileName: string, photoId: number) => {
    try {
      console.log(`Loading image: ${fileName} for photo ID: ${photoId}`);
      const imageUrl = getImageUrl(fileName);
      console.log(`Generated image URL: ${imageUrl}`);
      
      const resolvedUrl = await loadImageWithFallback(imageUrl);
      console.log(`Resolved image URL: ${resolvedUrl}`);
      
      setLoadedImages(prev => ({
        ...prev,
        [`${photoId}`]: resolvedUrl
      }));
    } catch (error) {
      console.error(`Error loading image ${fileName}:`, error);
      // Use a placeholder in case of error
      setLoadedImages(prev => ({
        ...prev,
        [`${photoId}`]: '/placeholder-image.svg'
      }));
    }
  };

  // Load images when issue changes
  useEffect(() => {
    const loadIssueImages = async () => {
      if (issue?.photos?.length) {
        console.log(`Loading ${issue.photos.length} images for issue ${issue.id}`);
        for (const photo of issue.photos) {
          await handleImageLoad(photo.file_name, photo.id);
        }
      }
    };

    if (issue) {
      loadIssueImages();
    }
  }, [issue]);

  useEffect(() => {
    const fetchData = async () => {
      if (!projectId || !issueId) {
        setError('Missing project or issue ID');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        const parsedProjectId = parseInt(projectId);
        const parsedIssueId = parseInt(issueId);

        if (isNaN(parsedProjectId) || isNaN(parsedIssueId)) {
          setError('Invalid project or issue ID');
          return;
        }

        // Fetch project first
        await fetchProject(parsedProjectId);

        // Fetch lines for this project
        try {
          await fetchLines({ project_id: parsedProjectId });
        } catch (e) {
          console.warn('Failed to fetch lines for project', e);
        }

        // Fetch project users (IDs) and all active users
        try {
          const pUsers = await projectUserApi.getProjectUsers(parsedProjectId);
          setProjectUserIds(pUsers);
        } catch (e) {
          console.warn('Failed to fetch project users', e);
        }

        try {
          const fetchedUsers = await userApi.getUsers();
          setUsers(fetchedUsers.filter((u: User) => u.is_active));
        } catch (e) {
          console.warn('Failed to fetch users', e);
        }

        // Then fetch issue with user details
        await fetchIssueData();
      } catch (err) {
        console.error('Error fetching data:', err);
        setError(err instanceof Error ? err.message : 'Failed to fetch data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [projectId, issueId, fetchProject]);

  // Role-filtered user lists for read-only selects
  const cmPicUsers = useMemo(() => {
    return users.filter(u => {
      if (!projectUserIds.includes(u.id)) return false;
      if (!u.roles) return false;
      return u.roles.some(r => (typeof r === 'string' ? r === 'cm_pic' : r === UserRole.CM_PIC));
    });
  }, [users, projectUserIds]);

  const reviewerUsers = useMemo(() => {
    return users.filter(u => {
      if (!projectUserIds.includes(u.id)) return false;
      if (!u.roles) return false;
      return u.roles.some(r => (typeof r === 'string' ? r === 'reviewer' : r === UserRole.REVIEWER));
    });
  }, [users, projectUserIds]);

  const approverUsers = useMemo(() => {
    return users.filter(u => {
      if (!projectUserIds.includes(u.id)) return false;
      if (!u.roles) return false;
      return u.roles.some(r => (typeof r === 'string' ? r === 'approver' : r === UserRole.APPROVER));
    });
  }, [users, projectUserIds]);

  // Completion date in YYYY-MM-DD for date field
  const completionDateValue = useMemo(() => {
    if (!issue) return '';
    const src = issue.completion_date || issue.target_date;
    if (!src) return '';
    return src.split('T')[0];
  }, [issue]);

  // Create a function to fetch issue data
  const fetchIssueData = async () => {
    if (!issueId) return;
    
    try {
      setLoading(true);
      const parsedIssueId = parseInt(issueId);
      
      // Fetch issue with user details
      const issueData = await issueApi.getIssue(
        parsedIssueId,
        projectId ? parseInt(projectId, 10) : undefined,
        true
      );
      setIssue(issueData);
    } catch (err) {
      console.error('Error fetching issue data:', err);
    } finally {
      setLoading(false);
    }
  };

  // Handle approve action
  const handleApprove = async () => {
    if (!issue || !issueId) return;
    
    setSubmitLoading(true);
    setSubmitError(null);
    
    try {
      let updateData: IssueUpdate = {};
      
      // Different logic based on user role and issue status
      if (user && isCmPic(user) && issue.status === 'waiting_response') {
        // CM_PIC responding to an issue in waiting_response status
        updateData = {
          status: 'waiting_review',
          final_counter_measure: finalCounterMeasure
        };
        
        // Upload counter measure photos if any are selected
        if (selectedCounterMeasureFiles.length > 0) {
          try {
            await issueApi.uploadPhotos(
              parseInt(issueId),
              selectedCounterMeasureFiles,
              'counter_measure',
              projectId ? parseInt(projectId, 10) : undefined
            );
          } catch (error) {
            console.error('Error uploading counter measure photos:', error);
            setSubmitError('Failed to upload counter measure photos. Please try again.');
            return;
          }
        }
      } else if (user && isReviewer(user) && issue.status === 'waiting_review') {
        // Reviewer responding to an issue in waiting_review status
        updateData = {
          status: 'waiting_approval'
        };
      } else if (user && isApprover(user) && 
                (issue.status === 'waiting_review' || issue.status === 'waiting_approval')) {
        // Approver approving an issue in waiting_review or waiting_approval status
        updateData = {
          status: 'completed',
          final_counter_measure: finalCounterMeasure
        };
      }
      
      // Add notes if provided - this will be included in the issue history
      if (notes.trim()) {
        updateData.notes = notes;
      }
      
      // Add a history entry flag to indicate this is an approval action
      updateData.is_approved = true;
      
      await issueApi.updateIssue(
        parseInt(issueId),
        updateData,
        projectId ? parseInt(projectId, 10) : undefined
      );
      
      // Reload the issue data to get the updated history
      await fetchIssueData();
      
      setApproveDialogOpen(false);
      setCounterMeasure('');
      setFinalCounterMeasure('');
      setNotes('');
    } catch (error) {
      console.error('Error approving issue:', error);
      setSubmitError('Failed to approve issue. Please try again.');
    } finally {
      setSubmitLoading(false);
    }
  };
  
  // Handle reject action
  const handleReject = async () => {
    if (!issue || !issueId) return;
    
    setSubmitLoading(true);
    setSubmitError(null);
    
    try {
      let updateData: IssueUpdate = {};
      
      // Different logic based on user role and issue status
      if (user && isCmPic(user) && issue.status === 'waiting_response') {
        // CM_PIC rejecting an issue in waiting_response status
        // Change status back to draft
        updateData = {
          status: 'draft'
        };
      } else if (user && isReviewer(user) && issue.status === 'waiting_review') {
        // Reviewer rejecting an issue in waiting_review status
        // Set status back to waiting_response
        updateData = {
          status: 'waiting_response'
        };
      } else if (user && isApprover(user) && issue.status === 'waiting_approval') {
        // Approver rejecting an issue in waiting_review or waiting_approval status
        // Set status back to waiting_response
        updateData = {
          status: 'waiting_review'
        };
      }
      
      // Add notes if provided - this will be included in the issue history
      if (notes.trim()) {
        updateData.notes = notes;
      }
      
      // Add a history entry flag to indicate this is a rejection action
      updateData.is_approved = false;
      
      await issueApi.updateIssue(
        parseInt(issueId),
        updateData,
        projectId ? parseInt(projectId, 10) : undefined
      );
      
      // Reload the issue data to get the updated history
      await fetchIssueData();
      
      setRejectDialogOpen(false);
      setNotes('');
    } catch (error) {
      console.error('Error rejecting issue:', error);
      setSubmitError('Failed to reject issue. Please try again.');
    } finally {
      setSubmitLoading(false);
    }
  };

  if (loading || projectLoading) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
        <Typography variant="h6" sx={{ mr: 2 }}>Loading...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(`/projects/${projectId}/issues`)}
          sx={{ mt: 2 }}
        >
          Back to Issues
        </Button>
      </Box>
    );
  }

  if (!issue || !currentProject) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">Issue or project not found</Alert>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(`/projects/${projectId}/issues`)}
          sx={{ mt: 2 }}
        >
          Back to Issues
        </Button>
      </Box>
    );
  }

  console.log('Render state:', { loading, error, issue, currentProject });

  const formatWord = (word: string) => {
    if (!word) return '';
    return word
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const months = [
      "Jan", "Feb", "Mar", "Apr", "May", "Jun",
      "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"
    ];

    const day = date.getDate();
    const month = months[date.getMonth()];
    const year = date.getFullYear();

    const formattedDay = day < 10 ? `0${day}` : day;

    return `${formattedDay}-${month}-${year}`;
  };

  const formatDateTime = (dateString: string) => {
    const date = new Date(dateString);
    const months = [
      "Jan", "Feb", "Mar", "Apr", "May", "Jun",
      "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"
    ];

    const day = date.getDate();
    const month = months[date.getMonth()];
    const year = date.getFullYear();

    const formattedDay = day < 10 ? `0${day}` : day;

    // ensure time in GMT+7 region
    const gmt7Date = new Date(date.getTime() + 7 * 60 * 60 * 1000);

    return `${formattedDay}-${month}-${year} ${gmt7Date.getHours()}:${gmt7Date.getMinutes()}`;
  };


  // Get the status percentage for progress display
  const getStatusPercentage = (status: string) => {
    if (!status) return 0;
    
    const statusLower = status.toLowerCase();
    switch (statusLower) {
      case 'waiting_response':
        return 0.25;
      case 'waiting_review':
        return 0.5;
      case 'waiting_approval':
        return 0.75;
      case 'completed':
        return 1;
      default:
        return 0;
    }
  };

  // Get the progress image based on status
  const getProgressImage = (status: string) => {
    if (!status) return null;
    
    const percentage = getStatusPercentage(status);
    
    if (percentage <= 0) {
      return null;
    } else if (percentage <= 0.25) {
      return 'progress-25.png';
    } else if (percentage <= 0.5) {
      return 'progress-50.png';
    } else if (percentage <= 0.75) {
      return 'progress-75.png';
    } else {
      return 'progress-100.png';
    }
  };

  // Export to PDF function
  const exportToPDF = async () => {
    if (!contentRef.current || !issue || !currentProject) return;
    
    try {
      // Get the content element
      const content = contentRef.current;
      
      // Format current date and time for filename
      const now = new Date();
      const dateTimeStr = format(now, 'yyyyMMdd_HHmmss');
      
      // Create filename with project code and name
      const projectCode = currentProject.code ? currentProject.code : '';
      const projectName = currentProject.name.replace(/\s+/g, '_');
      const fileName = projectCode 
        ? `${projectCode}_${projectName}_bira_${issue.id}_${dateTimeStr}.pdf`
        : `${projectName}_bira_${issue.id}_${dateTimeStr}.pdf`;
      
      // Set up loading indicator
      setLoading(true);
      
      // Capture the content as canvas with reduced scale for smaller file size
      const canvas = await html2canvas(content, {
        scale: 1.2, // Reduced scale for better file size while maintaining readability
        useCORS: true, // Enable CORS for images
        logging: false,
        onclone: (document) => {
          // Make any adjustments to the cloned document before capturing
          const element = document.getElementById('content-to-print');
          if (element) {
            element.style.padding = '20px';
            element.style.backgroundColor = 'white';
          }
        }
      });
      
      // Calculate PDF dimensions based on content aspect ratio
      const imgWidth = 210; // A4 width in mm
      const pageHeight = 297; // A4 height in mm
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      
      // Create PDF with A4 dimensions
      const pdf = new jsPDF('p', 'mm', 'a4');
      
      // Add title
      pdf.setFontSize(16);
      pdf.text(`BIRA #${issue.id} - ${currentProject.name}`, 10, 10);
      
      // Add generation date
      pdf.setFontSize(10);
      pdf.text(`Generated on: ${format(now, 'PPP')} at ${format(now, 'p')}`, 10, 20);
      
      // Add content
      let position = 30; // Start position after the title
      
      // Add the canvas image to the PDF using JPEG format with compression for smaller file size
      // Quality set to 0.8 (80%) for good balance between quality and file size
      pdf.addImage(canvas.toDataURL('image/jpeg', 0.8), 'JPEG', 0, position, imgWidth, imgHeight);
      
      // Save the PDF
      pdf.save(fileName);
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Failed to generate PDF. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Check if the current user can edit the issue
  const canEditIssue = () => {
    if (!user || !issue) return false;
    
    // Allow edit if user is admin
    if (isAdmin(user)) return true;
    
    // Allow edit if user has the Issues role
    if (hasRole(user, UserRole.ISSUER)) return true;
    
    // Allow edit if user is the creator of the issue AND not CM/PIC only
    return user.id === issue.created_by && isIssuer(user);
  };

  // Check if the current user can delete the issue
  const canDeleteIssue = () => {
    if (!user || !issue) return false;
    
    // Only allow delete if issue status is 'waiting_response', 'waiting_review', or 'draft'
    if (
      issue.status !== 'waiting_response' &&
      issue.status !== 'waiting_review' &&
      issue.status !== 'draft'
    )
      return false;
    
    // Allow delete if user is admin
    if (isAdmin(user)) return true;
    
    // Allow delete if user has the Issues role
    if (hasRole(user, UserRole.ISSUER)) return true;
    
    // Allow delete if user is the creator of the issue AND not CM/PIC only
    return user.id === issue.created_by && isIssuer(user);
  };
  
  // Handle delete issue
  const handleDeleteIssue = async () => {
    if (!issue || !issueId) return;
    
    setSubmitLoading(true);
    setSubmitError(null);
    
    try {
      await issueApi.deleteIssue(
        parseInt(issueId),
        projectId ? parseInt(projectId, 10) : undefined
      );
      
      // Navigate back to the project page after successful deletion
      navigate(`/projects/${issue.project_id}`);
    } catch (error) {
      console.error('Error deleting issue:', error);
      setSubmitError('Failed to delete issue. Please try again.');
      setDeleteDialogOpen(false);
    } finally {
      setSubmitLoading(false);
    }
  };

  // Styles
  const boxBgColor = '#E9E9E8';
  const boxBorderedLeft = {
    display: 'block', height: '100%', border: 1, bgcolor: boxBgColor
  };
  const boxBorderedLeft2 = {
    display: 'block', height: '100%', borderLeft: 1, borderBottom: 1, borderRight: 1, bgcolor: boxBgColor
  };
  const boxBorderedRight = {
    display: 'block', height: '100%', borderTop: 1, borderRight: 1, borderBottom: 1
  };
  const boxBorderedRight2 = {
    display: 'block', height: '100%', borderRight: 1, borderBottom: 1
  };
  // End Styles

  return (
    <Box sx={{ p: 3 }}>
      <Breadcrumbs sx={{ mb: 2 }}>
        <Link
          component="button"
          variant="body1"
          onClick={() => navigate('/projects')}
          sx={{ textDecoration: 'none' }}
        >
          Events
        </Link>
        <Link
          component="button"
          variant="body1"
          onClick={() => navigate(`/projects/${issue.project_id}`)}
          sx={{ textDecoration: 'none' }}
        >
          {currentProject.name}
        </Link>
        <Typography color="text.primary">BIRA {issue.id}</Typography>
      </Breadcrumbs>

      <Paper sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <IconButton onClick={() => navigate(`/projects/${issue.project_id}`)} sx={{ mr: 2 }}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h5" component="h1" sx={{ flexGrow: 1 }}>
            BIRA #{issue?.id || ''}
          </Typography>
          <Tooltip title="Export to PDF">
            <IconButton 
              color="primary" 
              onClick={exportToPDF}
              sx={{ mr: 2 }}
            >
              <FileDownloadIcon />
            </IconButton>
          </Tooltip>
          {canEditIssue() && issue.status !== 'completed' && (
            <Button
              variant="contained"
              startIcon={<EditIcon />}
              onClick={() => navigate(`/projects/${issue.project_id}/issues/${issue.id}/edit`)}
              sx={{ mr: 1 }}
            >
              Edit BIRA
            </Button>
          )}
          {canDeleteIssue() && (
            <Button
              variant="contained"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={() => setDeleteDialogOpen(true)}
            >
              Delete BIRA
            </Button>
          )}
        </Box>

        <Divider sx={{ mb: 3 }} />

        <div ref={contentRef} id="content-to-print">
          {/* Main Grid Container */}
          <Grid container spacing={2}>
            {/* Left Column - read-only form elements */}
            <Grid item xs={12} md={4}>
              <Divider textAlign="left" sx={{ mb: 2 }}>Problem Details</Divider>

              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={12} md={12}>
                  <TextField
                    label="Type"
                    value={issue.type === 'general' ? 'General' : 'Process'}
                    fullWidth
                    InputProps={{ readOnly: true, sx: { bgcolor: '#f5f5f5' } }}
                  />
                </Grid>
              </Grid>

              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={12} md={12}>
                  <TextField
                    label="Rank"
                    value={issue.rank?.toUpperCase() || ''}
                    fullWidth
                    InputProps={{ readOnly: true, sx: { bgcolor: '#f5f5f5' } }}
                  />
                </Grid>
              </Grid>

              {issue.type === 'process' && (
                <>
                  <Grid container spacing={2} sx={{ mb: 2 }}>
                    <Grid item xs={12} md={12}>
                      <TextField
                        label="Line"
                        value={lines.find(line => line.id === issue.line_id)?.name || ''}
                        fullWidth
                        InputProps={{ readOnly: true, sx: { bgcolor: '#f5f5f5' } }}
                      />
                    </Grid>
                  </Grid>

                  <Grid container spacing={2} sx={{ mb: 2 }}>
                    <Grid item xs={12} md={12}>
                      <TextField
                        label="Part Number"
                        value={issue.part_number || ''}
                        fullWidth
                        InputProps={{ readOnly: true, sx: { bgcolor: '#f5f5f5' } }}
                      />
                    </Grid>
                  </Grid>

                  <Grid container spacing={2} sx={{ mb: 2 }}>
                    <Grid item xs={12} md={12}>
                      <TextField
                        label="Part Name"
                        value={issue.part_name || ''}
                        fullWidth
                        InputProps={{ readOnly: true, sx: { bgcolor: '#f5f5f5' } }}
                      />
                    </Grid>
                  </Grid>
                </>
              )}

              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={12} md={12}>
                  <TextField
                    label="Issuer"
                    value={issue.created_by_name || 'N/A'}
                    fullWidth
                    InputProps={{ readOnly: true, sx: { bgcolor: '#f5f5f5' } }}
                  />
                </Grid>
              </Grid>

              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={12} md={12}>
                  <TextField
                    label="Issued Date"
                    value={issue.created_at ? format(new Date(issue.created_at), 'dd MMM yyyy') : issue.created_on ? format(new Date(issue.created_on), 'dd MMM yyyy') : 'N/A'}
                    fullWidth
                    InputProps={{ readOnly: true, sx: { bgcolor: '#f5f5f5' } }}
                  />
                </Grid>
              </Grid>

              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={12} md={12}>
                  <TextField
                    label="Status"
                    value={formatWord(issue.status) || ''}
                    fullWidth
                    InputProps={{ readOnly: true, sx: { bgcolor: '#f5f5f5' } }}
                  />
                </Grid>
              </Grid>

              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={12} md={12}>
                  <TextField
                    label="C/M PIC"
                    value={cmPicUsers.find(u => u.id === issue.pic_id)?.full_name || ''}
                    fullWidth
                    InputProps={{ readOnly: true, sx: { bgcolor: '#f5f5f5' } }}
                  />
                </Grid>
              </Grid>

              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={12} md={12}>
                  <TextField
                    label="Reviewer"
                    value={reviewerUsers.find(u => u.id === issue.reviewer_pic_id)?.full_name || ''}
                    fullWidth
                    InputProps={{ readOnly: true, sx: { bgcolor: '#f5f5f5' } }}
                  />
                </Grid>
              </Grid>

              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={12} md={12}>
                  <TextField
                    label="Approver"
                    value={approverUsers.find(u => u.id === issue.approver_pic_id)?.full_name || ''}
                    fullWidth
                    InputProps={{ readOnly: true, sx: { bgcolor: '#f5f5f5' } }}
                  />
                </Grid>
              </Grid>

              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={12} md={12}>
                  <TextField
                    label="Target Completion Date"
                    type="date"
                    value={completionDateValue}
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    InputProps={{ readOnly: true, sx: { bgcolor: '#f5f5f5' } }}
                  />
                </Grid>
              </Grid>

              <Box sx={{ mb: 2, mt: 2 }}>
                <Divider textAlign="left" sx={{ mb: 2 }}>Progress</Divider>
                <Box sx={{ position: 'relative', width: '80px', height: '80px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  {(() => {
                    const percentage = getStatusPercentage(issue.status);
                    let imageName = 'progress-25.png';
                    
                    if (percentage <= 0) {
                      // For 0% progress, just show the text without an image
                      return (
                        <Box 
                          sx={{ 
                            position: 'relative', 
                            width: '80px', 
                            height: '80px',
                            border: '1px solid #ccc',
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                        >
                          <Typography
                            variant="caption"
                            sx={{
                              fontSize: '10px',
                              fontWeight: 'bold'
                            }}
                          >
                            0%
                          </Typography>
                        </Box>
                      );
                    } else if (percentage <= 0.25) {
                      imageName = 'progress-25.png';
                    } else if (percentage <= 0.5) {
                      imageName = 'progress-50.png';
                    } else if (percentage <= 0.75) {
                      imageName = 'progress-75.png';
                    } else {
                      imageName = 'progress-100.png';
                    }
                    
                    return (
                      <Box sx={{ position: 'relative', width: '80px', height: '80px' }}>
                        <img 
                          src={`${process.env.PUBLIC_URL}/${imageName}`} 
                          alt={`${Math.round(percentage * 100)}% complete`}
                          style={{ width: '100%', height: '100%' }}
                        />
                      </Box>
                    );
                  })()}
                </Box>
              </Box>

            </Grid> {/* End Left Side */}

            {/* Right Side */}
            <Grid item xs={12} md={8}>
              <Divider textAlign="left" sx={{ mb: 2 }}>Problem Information</Divider>
              
              <Box sx={{ mb: 2, p: 2, border: 1, borderColor: 'divider', borderRadius: 1 }}>
                <Typography variant="subtitle2" color="text.secondary">Problem Description</Typography>
                <Typography variant="body1">{issue.problem || "N/A"}</Typography>
              </Box>
              
              <Divider textAlign="left" sx={{ mb: 2 }}>Problem Pictures</Divider>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
                {issue.photos && issue.photos.filter(p => p.photo_type === 'problem').length > 0 ? (
                  issue.photos.filter(p => p.photo_type === 'problem').map((photo) => (
                    <Box
                      key={photo.id}
                      sx={{
                        position: 'relative',
                        width: 200,
                        height: 200,
                        borderRadius: 2,
                        overflow: 'hidden',
                        boxShadow: 1,
                      }}
                    >
                      <Box
                        component="img"
                        src={loadedImages[`${photo.id}`] || getImageUrl(photo.file_name)}
                        alt={`Issue photo ${photo.id}`}
                        onError={(e) => {
                          console.error(`Error loading image in img tag: ${photo.file_name}`);
                          // Set a fallback image on error
                          (e.target as HTMLImageElement).src = '/placeholder-image.svg';
                        }}
                        sx={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                          cursor: 'pointer',
                        }}
                        onClick={() => setSelectedImage(loadedImages[`${photo.id}`] || getImageUrl(photo.file_name))}
                      />
                    </Box>
                  ))
                ) : (
                  <Typography variant="body2" color="text.secondary">No photos available</Typography>
                )}
              </Box>
              <Box sx={{ mb: 2, mt: 2 }}>
                <Divider textAlign="left" sx={{ mb: 2 }}>Counter Measure</Divider>
                <Box sx={{ mb: 2, p: 2, border: 1, borderColor: 'divider', borderRadius: 1 }}>
                  <Typography variant="subtitle2" color="text.secondary">Counter Measure Request</Typography>
                  <Typography variant="body1">{issue.counter_measure || "N/A"}</Typography>
                </Box>
                <Divider textAlign="left" sx={{ mb: 2 }}>Final Counter Measure</Divider>
                <Box sx={{ mb: 2, p: 2, border: 1, borderColor: 'divider', borderRadius: 1 }}>
                  <Typography variant="subtitle2" color="text.secondary">Final Counter Measure</Typography>
                  <Typography variant="body1">{issue.final_counter_measure || "N/A"}</Typography>
                </Box>
                <Divider textAlign="left" sx={{ mb: 2 }}>Final Counter Measure Pictures</Divider>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
                  {issue.photos && issue.photos.filter(p => p.photo_type === 'counter_measure').length > 0 ? (
                    issue.photos.filter(p => p.photo_type === 'counter_measure').map((photo) => (
                      <Box
                        key={photo.id}
                        sx={{
                          position: 'relative',
                          width: 200,
                          height: 200,
                          borderRadius: 2,
                          overflow: 'hidden',
                          boxShadow: 1,
                        }}
                      >
                        <Box
                          component="img"
                          src={loadedImages[`${photo.id}`] || getImageUrl(photo.file_name)}
                          alt={`Counter measure photo ${photo.id}`}
                          onError={(e) => {
                            console.error(`Error loading image in img tag: ${photo.file_name}`);
                            (e.target as HTMLImageElement).src = '/placeholder-image.svg';
                          }}
                          sx={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                            cursor: 'pointer',
                          }}
                          onClick={() => setSelectedImage(loadedImages[`${photo.id}`] || getImageUrl(photo.file_name))}
                        />
                      </Box>
                    ))
                  ) : (
                    <Typography variant="body2" color="text.secondary">No photos available</Typography>
                  )}
                </Box>
              </Box>
            </Grid> {/* End Right Side */}

          </Grid>

          {/* History Section */}
          <Divider textAlign="left" sx={{ mb: 2 }}>History</Divider>
          <Grid container sx={{ mb: 2 }}>
            <Grid item md={12}>
              <Box sx={{ display: 'block', height: '100%', border: 1 }}>
                <TableContainer>
                  <Table sx={{ minWidth: 650 }} aria-label="simple table">
                    <TableHead>
                      <TableRow>
                        <TableCell>STATUS</TableCell>
                        <TableCell>ACTION</TableCell>
                        <TableCell>PIC</TableCell>
                        <TableCell>DATE TIME</TableCell>
                        <TableCell>NOTES</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {issue.history && issue.history.length > 0 ? (
                        issue.history.map((historyItem) => (
                          <TableRow
                            key={historyItem.id}
                            sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                          >
                            <TableCell>{formatWord(historyItem.status)}</TableCell>
                            <TableCell>{formatWord(historyItem.action)}</TableCell>
                            <TableCell>{historyItem.pic_name || 'N/A'}</TableCell>
                            <TableCell>{formatDateTime(historyItem.created_on)}</TableCell>
                            <TableCell>{historyItem.notes || 'No notes'}</TableCell>
                          </TableRow>
                        ))
                      ) : (
                        <TableRow>
                          <TableCell colSpan={5} align="center">No history records found</TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            </Grid>
          </Grid> 

          {/* Approval Section */}
          {issue && user && 
            ((isCmPic(user) && issue.status === 'waiting_response' && issue.pic_id === user.id) ||
             (isReviewer(user) && issue.status === 'waiting_review' && issue.reviewer_pic_id === user.id) || 
             (isApprover(user) && ['waiting_review', 'waiting_approval'].includes(issue.status) && issue.approver_pic_id === user.id)) && (
            <Grid container sx={{ mb: 2 }}>
              <Grid item md={12}>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<VerifiedIcon />}
                  sx={{ mr: 2 }}
                  onClick={() => {
                    // Clear fields before opening dialog
                    setFinalCounterMeasure('');
                    setApproveDialogOpen(true);
                  }}
                >
                  {user && ((isCmPic(user) && issue.status === 'waiting_response') || (isReviewer(user) && issue.status === 'waiting_review')) ? 'Response' : 'Approve'}
                </Button>
                <Button
                  variant="contained"
                  color="error"
                  startIcon={<ThumbDownIcon />}
                  onClick={() => setRejectDialogOpen(true)}
                >
                  Reject
                </Button>
              </Grid>
            </Grid>
          )}
        </div>
      </Paper>

      {/* Image Dialog */}
      <Dialog
        open={!!selectedImage}
        onClose={() => setSelectedImage(null)}
        maxWidth="lg"
        fullWidth
      >
        <DialogContent sx={{ p: 0, position: 'relative' }}>
          {selectedImage && (
            <>
              <IconButton
                onClick={() => setSelectedImage(null)}
                sx={{
                  position: 'absolute',
                  right: 8,
                  top: 8,
                  bgcolor: 'background.paper',
                  '&:hover': {
                    bgcolor: 'background.paper',
                  },
                }}
              >
                <CloseIcon />
              </IconButton>
              <Box
                component="img"
                src={selectedImage}
                alt="Full size"
                sx={{
                  width: '100%',
                  height: 'auto',
                  maxHeight: '90vh',
                  objectFit: 'contain',
                }}
              />
            </>
          )}
        </DialogContent>
      </Dialog>
      
      {/* Approve Dialog */}
      <Dialog
        open={approveDialogOpen}
        onClose={() => setApproveDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {user && isCmPic(user) && issue?.status === 'waiting_response' ? 'Response to BIRA' : 'Approve BIRA'}
          <IconButton
            aria-label="close"
            onClick={() => setApproveDialogOpen(false)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {submitError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {submitError}
            </Alert>
          )}
          
          {/* Different form fields based on user role and issue status */}
          {user && issue && isCmPic(user) && issue.status === 'waiting_response' && (
            <>
              <FormControl fullWidth sx={{ mt: 2 }}>
                <FormLabel>FINAL COUNTER MEASURE</FormLabel>
                <TextField
                  multiline
                  rows={4}
                  value={finalCounterMeasure}
                  onChange={(e) => setFinalCounterMeasure(e.target.value)}
                  fullWidth
                  placeholder="Enter final counter measure"
                  sx={{ mt: 1 }}
                />
              </FormControl>
              
              {/* Counter Measure Photo Upload Section */}
              <Box sx={{ mt: 3 }}>
                <FormLabel>COUNTER MEASURE PHOTOS</FormLabel>
                <Box sx={{ mt: 1 }}>
                  <input
                    accept="image/*"
                    id="counter-measure-photo-upload"
                    type="file"
                    multiple
                    onChange={handleFileChange}
                    style={{ display: 'none' }}
                    ref={counterMeasureFileInputRef}
                  />
                  <label htmlFor="counter-measure-photo-upload">
                    <Button
                      variant="outlined"
                      component="span"
                      startIcon={<PhotoCamera />}
                      disabled={isCompressing}
                    >
                      Upload Photos
                    </Button>
                  </label>
                </Box>
                
                {/* Selected Files Preview */}
                {selectedCounterMeasureFiles.length > 0 && (
                  <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {selectedCounterMeasureFiles.map((file, index) => (
                      <Box
                        key={`${file.name}-${index}`}
                        sx={{
                          position: 'relative',
                          width: 100,
                          height: 100,
                          border: '1px solid #ccc',
                          borderRadius: 1,
                          overflow: 'hidden',
                        }}
                      >
                        <img
                          src={URL.createObjectURL(file)}
                          alt={`Preview ${index}`}
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                          }}
                        />
                        <IconButton
                          size="small"
                          sx={{
                            position: 'absolute',
                            top: 0,
                            right: 0,
                            backgroundColor: 'rgba(255, 255, 255, 0.7)',
                            '&:hover': {
                              backgroundColor: 'rgba(255, 255, 255, 0.9)',
                            },
                          }}
                          onClick={() => handleRemoveFile(index)}
                        >
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    ))}
                  </Box>
                )}
                
                {isCompressing && (
                  <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                    <CircularProgress size={20} sx={{ mr: 1 }} />
                    <Typography variant="body2">Compressing images...</Typography>
                  </Box>
                )}
              </Box>
            </>
          )}
          
          {user && issue && isApprover(user) && 
           (issue.status === 'waiting_review' || issue.status === 'waiting_approval') && (
            <FormControl fullWidth sx={{ mt: 2 }}>
            <FormLabel>Notes</FormLabel>
            <TextField
              multiline
              rows={4}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              fullWidth
              placeholder="Enter remark/reason"
              sx={{ mt: 1 }}
            />
          </FormControl>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApproveDialogOpen(false)}>Cancel</Button>
          <Button 
            variant="contained" 
            color="primary" 
            onClick={handleApprove}
            disabled={
              submitLoading || 
              !!(user && isCmPic(user) && issue?.status === 'waiting_response' && !finalCounterMeasure.trim())
              // Notes are optional for approvers, so we don't disable the button if notes are empty
            }
          >
            {submitLoading ? 'Processing...' : 'Submit'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Reject Dialog */}
      <Dialog
        open={rejectDialogOpen}
        onClose={() => setRejectDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Reject BIRA
          <IconButton
            aria-label="close"
            onClick={() => setRejectDialogOpen(false)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {submitError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {submitError}
            </Alert>
          )}
          <FormControl fullWidth sx={{ mt: 2 }}>
            <FormLabel>Notes</FormLabel>
            <TextField
              multiline
              rows={4}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              fullWidth
              placeholder="Enter remark/reason"
              sx={{ mt: 1 }}
            />
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectDialogOpen(false)}>Cancel</Button>
          <Button 
            variant="contained" 
            color="error" 
            onClick={handleReject}
            disabled={!notes.trim() || submitLoading}
          >
            {submitLoading ? 'Processing...' : 'Submit'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Delete BIRA
          <IconButton
            aria-label="close"
            onClick={() => setDeleteDialogOpen(false)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {submitError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {submitError}
            </Alert>
          )}
          <Box sx={{ mt: 2 }}>
            <Typography variant="body1">
              Are you sure you want to delete BIRA #{issue?.id}? This action cannot be undone.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button 
            variant="contained" 
            color="error" 
            onClick={handleDeleteIssue}
            disabled={submitLoading}
          >
            {submitLoading ? 'Processing...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ProjectIssueDetail;
